import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus } from 'lucide-react';
import authFetch from '../../utils/authFetch';

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';

const formatDateEu = (isoDate) => {
  if (!isoDate) return '';
  const [year, month, day] = String(isoDate).slice(0, 10).split('-');
  if (!year || !month || !day) return String(isoDate);
  return `${Number(day)}.${Number(month)}.${year}`;
};

export const MAX_ADULTS = 6;
export const MAX_CHILDREN = 6;
export const MAX_GUESTS = 8;

// Older bookings only have a total guest count; treat those guests as adults.
export const bookingAdults = (b) => b.adults ?? b.guests;
export const bookingChildren = (b) => b.children ?? 0;

export const formatGuests = (b) => {
  const children = bookingChildren(b);
  return children > 0 ? `${bookingAdults(b)} + ${children} ch.` : String(bookingAdults(b));
};

export const statusBadgeStyle = (status) => {
  const colors = {
    new: { bg: '#dbeafe', color: '#1d4ed8' },
    confirmed: { bg: '#dcfce7', color: '#15803d' },
    declined: { bg: '#fee2e2', color: '#b91c1c' },
    cancelled: { bg: '#f1f5f9', color: '#64748b' },
    pending_payment: { bg: '#fef3c7', color: '#b45309' },
    expired: { bg: '#f1f5f9', color: '#94a3b8' }
  };
  const c = colors[status] || colors.new;
  return { backgroundColor: c.bg, color: c.color, padding: '4px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 600, textTransform: 'capitalize', whiteSpace: 'nowrap' };
};

// 'pending_payment' -> 'pending payment'
export const formatStatus = (status) => String(status || '').replace(/_/g, ' ');

// Amount paid through Stripe Checkout on dobrereal.com, e.g. "Paid 493,75 €".
export const formatPaid = (b) => (b.amount_paid_cents
  ? `Paid ${new Intl.NumberFormat('sl-SI', { style: 'currency', currency: 'EUR' }).format(b.amount_paid_cents / 100)}`
  : '');

const emptyForm = {
  checkIn: '',
  checkOut: '',
  adults: 2,
  children: 0,
  fullName: '',
  email: '',
  phone: '',
  message: '',
  comments: '',
  hasPet: false,
  status: 'new'
};

const BookingsPage = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [showAddForm, setShowAddForm] = useState(false);
  const [addDraft, setAddDraft] = useState(emptyForm);

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);

  const loadBookings = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/short-term-rentals/bookings`);
      if (!response.ok) throw new Error('Failed to load bookings');
      const data = await response.json();
      setBookings(data.bookings || []);
    } catch (err) {
      setError(err.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, []);

  const startAdd = () => {
    setAddDraft(emptyForm);
    setShowAddForm(true);
    setEditingId(null);
  };

  const cancelAdd = () => {
    setShowAddForm(false);
    setAddDraft(emptyForm);
  };

  const handleAddSave = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/short-term-rentals/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addDraft)
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to create booking');
      }
      await loadBookings();
      cancelAdd();
    } catch (err) {
      setError(err.message || 'Failed to create booking');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (b) => {
    setEditingId(b.id);
    setEditDraft({
      checkIn: b.check_in,
      checkOut: b.check_out,
      adults: bookingAdults(b),
      children: bookingChildren(b),
      fullName: b.full_name,
      email: b.email || '',
      phone: b.phone || '',
      message: b.message || '',
      comments: b.comments || '',
      hasPet: Boolean(b.has_pet),
      status: b.status
    });
    setShowAddForm(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };

  const handleEditSave = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/short-term-rentals/bookings/${editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editDraft)
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to update booking');
      }
      await loadBookings();
      cancelEdit();
    } catch (err) {
      setError(err.message || 'Failed to update booking');
    } finally {
      setSaving(false);
    }
  };

  // Copies the guest's smart-home guidebook link (works only during the stay of a confirmed booking).
  const handleCopyGuestLink = async (b) => {
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/short-term-rentals/bookings/${b.id}/guest-link`, { method: 'POST' });
      if (!response.ok) throw new Error('Failed to create guest link');
      const { url } = await response.json();
      try {
        await navigator.clipboard.writeText(url);
        window.alert(`Guest link copied:\n${url}${b.status !== 'confirmed' ? '\n\nNote: the link only works once the booking is confirmed.' : ''}`);
      } catch {
        window.prompt('Guest link:', url);
      }
    } catch (err) {
      setError(err.message || 'Failed to create guest link');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this booking? This cannot be undone.')) return;
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/short-term-rentals/bookings/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete booking');
      setBookings((prev) => prev.filter((b) => b.id !== id));
    } catch (err) {
      setError(err.message || 'Failed to delete booking');
    }
  };

  return (
    <div>
      <button onClick={() => navigate('/short-term-rentals')} style={backButtonStyle}>
        <ArrowLeft size={16} /> Back
      </button>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h1 style={{ color: '#1e293b', marginBottom: '6px' }}>Bookings</h1>
          <p style={{ color: '#64748b', marginBottom: '20px' }}>Bookings received from Hišica pri orehu.</p>
        </div>
        <button onClick={startAdd} style={addButtonStyle}>
          <Plus size={16} /> Add Booking
        </button>
      </div>

      {error && <p style={{ color: '#dc2626', marginBottom: '16px' }}>{error}</p>}

      {showAddForm && (
        <div style={{ ...tableCardStyle, marginBottom: '20px' }}>
          <h3 style={{ marginTop: 0, color: '#1e293b' }}>New Booking</h3>
          <BookingForm draft={addDraft} setDraft={setAddDraft} />
          <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
            <button onClick={handleAddSave} disabled={saving} style={saveButtonStyle}>{saving ? 'Saving...' : 'Save'}</button>
            <button onClick={cancelAdd} style={cancelButtonStyle}>Cancel</button>
          </div>
        </div>
      )}

      <div style={tableCardStyle}>
        {loading ? (
          <p style={{ color: '#64748b' }}>Loading...</p>
        ) : bookings.length === 0 ? (
          <p style={{ color: '#64748b' }}>No bookings yet.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Check-in</th>
                  <th style={thStyle}>Check-out</th>
                  <th style={thStyle}>Guests</th>
                  <th style={thStyle}>Pet</th>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Phone</th>
                  <th style={thStyle}>Message</th>
                  <th style={thStyle}>Comments</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Received</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => {
                  if (editingId === b.id && editDraft) {
                    return (
                      <tr key={b.id} style={{ backgroundColor: '#f8fafc' }}>
                        <td style={tdStyle} colSpan={12}>
                          <BookingForm draft={editDraft} setDraft={setEditDraft} />
                          <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                            <button onClick={handleEditSave} disabled={saving} style={saveButtonStyle}>{saving ? 'Saving...' : 'Save'}</button>
                            <button onClick={cancelEdit} style={cancelButtonStyle}>Cancel</button>
                          </div>
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <tr key={b.id}>
                      <td style={tdStyle}>{formatDateEu(b.check_in)}</td>
                      <td style={tdStyle}>{formatDateEu(b.check_out)}</td>
                      <td style={tdStyle}>{formatGuests(b)}</td>
                      <td style={tdStyle}>{b.has_pet ? 'Yes' : 'No'}</td>
                      <td style={tdStyle}>{b.full_name}</td>
                      <td style={tdStyle}>{b.email || '-'}</td>
                      <td style={tdStyle}>{b.phone || '-'}</td>
                      <td style={{ ...tdStyle, maxWidth: '200px', whiteSpace: 'pre-wrap' }}>{b.message || '-'}</td>
                      <td style={{ ...tdStyle, maxWidth: '200px', whiteSpace: 'pre-wrap' }}>{b.comments || '-'}</td>
                      <td style={tdStyle}>
                        <span style={statusBadgeStyle(b.status)}>{formatStatus(b.status)}</span>
                        {b.amount_paid_cents ? <div style={{ fontSize: '12px', color: '#15803d', marginTop: '4px', whiteSpace: 'nowrap' }}>{formatPaid(b)}</div> : null}
                      </td>
                      <td style={tdStyle}>{formatDateEu(b.created_at)}</td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button onClick={() => startEdit(b)} style={editButtonStyle}>Edit</button>
                          <button onClick={() => handleCopyGuestLink(b)} style={editButtonStyle} title="Copy smart-home guidebook link for the guest">Guest link</button>
                          <button onClick={() => handleDelete(b.id)} style={deleteButtonStyle}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export const BookingForm = ({ draft, setDraft }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
    <div>
      <label style={labelStyle}>Check-in</label>
      <input type="date" value={draft.checkIn} onChange={(e) => setDraft({ ...draft, checkIn: e.target.value })} style={inputStyle} />
    </div>
    <div>
      <label style={labelStyle}>Check-out</label>
      <input type="date" value={draft.checkOut} onChange={(e) => setDraft({ ...draft, checkOut: e.target.value })} style={inputStyle} />
    </div>
    <div>
      <label style={labelStyle}>Adults</label>
      <select
        value={draft.adults}
        onChange={(e) => {
          const adults = Number(e.target.value);
          setDraft({ ...draft, adults, children: Math.min(Number(draft.children) || 0, MAX_GUESTS - adults) });
        }}
        style={inputStyle}
      >
        {Array.from({ length: MAX_ADULTS }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
    </div>
    <div>
      <label style={labelStyle}>Children</label>
      <select value={draft.children} onChange={(e) => setDraft({ ...draft, children: Number(e.target.value) })} style={inputStyle}>
        {Array.from({ length: Math.max(0, Math.min(MAX_CHILDREN, MAX_GUESTS - Number(draft.adults))) + 1 }, (_, i) => i).map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
    </div>
    <div>
      <label style={labelStyle}>Status</label>
      <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} style={inputStyle}>
        <option value="new">New</option>
        <option value="pending_payment">Pending payment</option>
        <option value="confirmed">Confirmed</option>
        <option value="declined">Declined</option>
        <option value="cancelled">Cancelled</option>
        <option value="expired">Expired</option>
      </select>
    </div>
    <div>
      <label style={labelStyle}>Pet</label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#1e293b', padding: '8px 0' }}>
        <input type="checkbox" checked={Boolean(draft.hasPet)} onChange={(e) => setDraft({ ...draft, hasPet: e.target.checked })} />
        Guests bring a pet
      </label>
    </div>
    <div>
      <label style={labelStyle}>Full name</label>
      <input type="text" value={draft.fullName} onChange={(e) => setDraft({ ...draft, fullName: e.target.value })} style={inputStyle} />
    </div>
    <div>
      <label style={labelStyle}>Email</label>
      <input type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} style={inputStyle} />
    </div>
    <div>
      <label style={labelStyle}>Phone</label>
      <input type="text" value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} style={inputStyle} />
    </div>
    <div style={{ gridColumn: '1 / -1' }}>
      <label style={labelStyle}>Message</label>
      <textarea value={draft.message} onChange={(e) => setDraft({ ...draft, message: e.target.value })} style={{ ...inputStyle, minHeight: '60px' }} />
    </div>
    <div style={{ gridColumn: '1 / -1' }}>
      <label style={labelStyle}>Comments (internal)</label>
      <textarea value={draft.comments} onChange={(e) => setDraft({ ...draft, comments: e.target.value })} style={{ ...inputStyle, minHeight: '60px' }} />
    </div>
  </div>
);

const backButtonStyle = { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '14px', marginBottom: '20px', padding: 0 };
const addButtonStyle = { display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '10px', padding: '10px 16px', cursor: 'pointer', fontSize: '14px', fontWeight: 600, marginBottom: '20px' };
const tableCardStyle = { backgroundColor: 'white', padding: '25px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '14px' };
const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' };
const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', color: '#1e293b' };
const labelStyle = { display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px', fontWeight: 600 };
const inputStyle = { width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box' };
const saveButtonStyle = { backgroundColor: '#059669', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 };
const cancelButtonStyle = { backgroundColor: '#f1f5f9', color: '#334155', border: 'none', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px' };
const editButtonStyle = { backgroundColor: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', fontSize: '12px' };
const deleteButtonStyle = { backgroundColor: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', fontSize: '12px' };

export default BookingsPage;
